import React, { useCallback } from 'react';
import type {
  Account,
  AfcSubmission,
  AfcSubmissionCandidateOpportunity,
  Candidate,
  CandidateLegalTechSkill,
  GroupedExperiences,
  Taxonomy,
} from '@axiom/validation';

import { Grid } from '../../layout/Grid/Grid';
import { GridRow } from '../../layout/Grid/GridRow';
import { GridColumn } from '../../layout/Grid/GridColumn';
import { Gutter } from '../../layout/Gutter/Gutter';
import { BioPdfCandidate } from '../BioPdf/BioPdfCandidate';
import { Layout } from '../../layout/Layout/Layout';
import { LayoutItem } from '../../layout/Layout/LayoutItem';
import { SubmissionPdfDescriptionMultiTalent } from '../SubmissionPdfDescriptionMultiTalent/SubmissionPdfDescriptionMultiTalent';
import { PdfWantToTakeAction } from '../PdfWantToTakeAction/PdfWantToTakeAction';
import { LightTheme } from '../../theme/LightTheme/LightTheme';
import { ClientEngagementSelfServiceNoTalentForPdf } from '../ClientEngagementSelfService/ClientEngagementSelfServiceNoTalentForPdf';
import { ClientEngagementNoTalentCardForPdf } from '../ClientEngagementNoTalentCard/ClientEngagementNoTalentCardForPdf';

import { SubmissionPdfEngagementDetails } from './SubmissionPdfEngagementDetails';
import { SubmissionPdfHeader } from './SubmissionPdfHeader';

export type SubmissionPdfTypes = {
  isDownloading?: boolean;
  accountId: Account['id'];
  submission?: AfcSubmission;
  candidateOpps: AfcSubmissionCandidateOpportunity[];
  allGroupedExperiences: GroupedExperiences[][];
  clientUrl: string;
  loginUrl: string;
  taxonomy: Taxonomy;
  allCandidateIds: string[];
  allLegalTechnicalSkills: CandidateLegalTechSkill[][];
};

export const SubmissionPdf = ({
  isDownloading,
  accountId,
  submission,
  candidateOpps,
  allGroupedExperiences,
  clientUrl,
  loginUrl,
  taxonomy,
  allCandidateIds,
  allLegalTechnicalSkills,
}: SubmissionPdfTypes) => {
  const getExperiences = useCallback(
    (id: Candidate['id']): GroupedExperiences[] => {
      return allGroupedExperiences.find(ge => ge[0]?.candidateId === id) || [];
    },
    [allGroupedExperiences, candidateOpps]
  );
  const getLegalTechnicalSkills = useCallback(
    (id: Candidate['id']): CandidateLegalTechSkill[] => {
      const index = allCandidateIds.indexOf(id);
      return allLegalTechnicalSkills[index] || [];
    },
    [allLegalTechnicalSkills, candidateOpps]
  );

  if (!submission) {
    return null;
  }

  const NoTalentFoundSection = submission.selfService ? (
    <ClientEngagementSelfServiceNoTalentForPdf />
  ) : (
    <ClientEngagementNoTalentCardForPdf />
  );

  return (
    <div className="k-pdf-export submissionPdf">
      <LightTheme>
        <Layout background="panelLight" name="SUBMISSION_PDF">
          <LayoutItem>
            <Grid>
              <GridRow gutterBottom="24px">
                <GridColumn>
                  <SubmissionPdfHeader submission={submission} />
                </GridColumn>
              </GridRow>
              <GridRow gutterBottom="32px">
                <GridColumn>
                  <SubmissionPdfDescriptionMultiTalent
                    loginUrl={loginUrl}
                    submission={submission}
                  />
                </GridColumn>
              </GridRow>
              <GridRow gutterBottom="32px">
                <GridColumn>
                  <SubmissionPdfEngagementDetails
                    isDownloading={isDownloading}
                    submission={submission}
                    taxonomy={taxonomy}
                  />
                </GridColumn>
              </GridRow>
              <GridRow>
                <GridColumn>
                  {candidateOpps.length > 0
                    ? candidateOpps.map(candidateOpp => (
                        <Gutter bottom="32px" key={candidateOpp.candidate.id}>
                          <BioPdfCandidate
                            isDownloading={isDownloading}
                            candidate={candidateOpp.candidate}
                            submissionDescription={
                              candidateOpp.submissionDescription
                            }
                            showCandidateReputation={
                              candidateOpp.showCandidateReputation
                            }
                            candidateReputationClarification={
                              candidateOpp.candidateReputationClarification
                            }
                            groupedExperiences={getExperiences(
                              candidateOpp.candidate.id
                            )}
                            submission={submission}
                            taxonomy={taxonomy}
                            removeLogo
                            legalTechSkills={getLegalTechnicalSkills(
                              candidateOpp.candidate.id
                            )}
                          />
                          {!!accountId && !!submission.submissionId && (
                            <Gutter horizontal="32px">
                              <PdfWantToTakeAction
                                candidate={candidateOpp.candidate}
                                accountId={accountId}
                                submissionId={submission.submissionId}
                                clientUrl={clientUrl}
                              />
                            </Gutter>
                          )}
                        </Gutter>
                      ))
                    : NoTalentFoundSection}
                </GridColumn>
              </GridRow>
            </Grid>
          </LayoutItem>
        </Layout>
      </LightTheme>
    </div>
  );
};
